import { FC, FocusEvent, MouseEvent, memo, useCallback, useContext, useState } from 'react'

import { ScreenContext } from 'react-components'
import { useTranslation } from '../../../hooks/locale'
import { CtaButtonText, StyledGhostButton } from '../common/styles'
import {
    AverageLine,
    Bar,
    BarSlot,
    BarsArea,
    ChartAxisLabel,
    ChartScroller,
    ChartTitle,
    CtaLabel,
    HeaderAverage,
    HeaderDates,
    HeaderPercent,
    HeaderRow,
    MobileWeekPanel,
    MonthLabel,
    Tooltip,
    TooltipLabel,
    TooltipPercent,
    TooltipValueSuffix,
} from './styles'

const HIDDEN_PANEL_STYLE = { display: 'none' } as const

export type ExploreSource = 'bar' | 'cta'

export interface ChartWeek {
    from: string
    to: string
    label: string
    monthLabel: string
    showMonthLabel: boolean
    barHeight: number
    valueText: string | null
    valueSuffix?: string
    tooltipText: string
    explorable: boolean
}

interface WeekChartProps {
    title: string
    highlightValue: string
    highlightLabel: string
    averageLabel: string | null
    averageLineBottom: number | null
    axisLabel: string
    noValueText: string
    weeks: ChartWeek[]
    isLoading: boolean
    onExplore: (week: ChartWeek, source: ExploreSource) => void
}

const barIndexFromEvent = (target: EventTarget): number | null => {
    const bar = (target as Element).closest<HTMLElement>('[data-chart-bar]')
    return bar ? Number(bar.dataset.index) : null
}

const WeekChart: FC<WeekChartProps> = ({
    title,
    highlightValue,
    highlightLabel,
    averageLabel,
    averageLineBottom,
    axisLabel,
    noValueText,
    weeks,
    isLoading,
    onExplore,
}) => {
    const { t } = useTranslation()
    const { isDesktop } = useContext(ScreenContext)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const handleChartMouseOver = useCallback((event: MouseEvent<HTMLElement>) => {
        const index = barIndexFromEvent(event.target)
        if (index !== null) {
            setActiveIndex(index)
        } else if (!(event.target as Element).closest('[data-chart-tooltip]')) {
            setActiveIndex(null)
        }
    }, [])

    const handleChartMouseLeave = useCallback(() => setActiveIndex(null), [])

    const handleChartFocus = useCallback((event: FocusEvent<HTMLElement>) => {
        const index = barIndexFromEvent(event.target)
        if (index !== null) {
            setActiveIndex(index)
        }
    }, [])

    const handleChartClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const index = barIndexFromEvent(event.target)
            if (index === null) {
                return
            }
            if (isDesktop) {
                onExplore(weeks[index], 'bar')
            } else {
                setActiveIndex(index)
            }
        },
        [isDesktop, onExplore, weeks],
    )

    const handleExploreActive = useCallback(() => {
        if (!isLoading) {
            onExplore(weeks[activeIndex ?? 0], 'cta')
        }
    }, [isLoading, onExplore, weeks, activeIndex])

    const renderValue = (week: ChartWeek) => (
        <TooltipPercent>
            {week.valueText ?? noValueText}
            {!!week.valueText && !!week.valueSuffix && <TooltipValueSuffix>{week.valueSuffix}</TooltipValueSuffix>}
        </TooltipPercent>
    )

    const cta = (
        <StyledGhostButton onClick={handleExploreActive}>
            <CtaButtonText>
                <CtaLabel data-loading={isLoading}>{t('priceAndAvailability.cta')}</CtaLabel>
            </CtaButtonText>
        </StyledGhostButton>
    )

    return (
        <>
            <ChartTitle>{title}</ChartTitle>
            <HeaderRow>
                <HeaderPercent>{highlightValue}</HeaderPercent>
                <HeaderDates>{highlightLabel}</HeaderDates>
            </HeaderRow>
            {!!averageLabel && <HeaderAverage>{averageLabel}</HeaderAverage>}
            <ChartScroller>
                <BarsArea
                    onMouseOver={isDesktop ? handleChartMouseOver : undefined}
                    onMouseLeave={isDesktop ? handleChartMouseLeave : undefined}
                    onFocus={handleChartFocus}
                    onClick={handleChartClick}
                >
                    {averageLineBottom !== null && <AverageLine $bottom={averageLineBottom} />}
                    {weeks.map((week, index) => {
                        return (
                            <BarSlot key={week.from}>
                                {isDesktop && (activeIndex ?? 0) === index && (
                                    <Tooltip
                                        data-chart-tooltip
                                        $align={index < 5 ? 'left' : index > weeks.length - 6 ? 'right' : 'center'}
                                        $barHeight={week.barHeight}
                                        $hidden={activeIndex === null}
                                    >
                                        {renderValue(week)}
                                        <TooltipLabel>{week.tooltipText}</TooltipLabel>
                                        {week.explorable && cta}
                                    </Tooltip>
                                )}
                                <Bar
                                    type="button"
                                    aria-label={`${week.valueText ?? noValueText} ${week.tooltipText}`}
                                    $height={week.barHeight}
                                    $active={index === activeIndex || (activeIndex === null && index === 0)}
                                    data-chart-bar
                                    data-index={index}
                                    disabled={isLoading}
                                />
                                {week.showMonthLabel && <MonthLabel>{week.monthLabel}</MonthLabel>}
                            </BarSlot>
                        )
                    })}
                </BarsArea>
            </ChartScroller>
            {!isDesktop && (
                <MobileWeekPanel style={activeIndex === null ? HIDDEN_PANEL_STYLE : undefined}>
                    {renderValue(weeks[activeIndex ?? 0])}
                    <TooltipLabel>{weeks[activeIndex ?? 0].tooltipText}</TooltipLabel>
                    {weeks[activeIndex ?? 0].explorable && cta}
                </MobileWeekPanel>
            )}
            <ChartAxisLabel>{axisLabel}</ChartAxisLabel>
        </>
    )
}

export default memo(WeekChart)
